import { useState } from 'react'
import type { CoverageRow } from '../types.js'
import { describeDay, SHIFT_TYPE_LABELS } from './preferencesText.js'

/**
 * The dates below the facility's minimum staffing (scheduling rules plan
 * phase D), shared by the run's result, the board card and the read-only
 * views: a line saying how many, then the gaps by date on request. Renders
 * nothing where the facility keeps no minimums.
 */

/** "2-South day RN: 1 of 2", or "... 1 of 3 (1 per 10 residents at a census of 25)" for a ratio line (F5). */
function describeGap(gap: CoverageRow, unitName: (unitId: string) => string): string {
  const unit = gap.unitId ? unitName(gap.unitId) : 'Every unit'
  const ratio = gap.perResidents && gap.census !== undefined ? ` (per ${gap.perResidents} residents at a census of ${gap.census})` : ''
  return `${unit} ${SHIFT_TYPE_LABELS[gap.shiftType].toLowerCase()}${gap.role ? ` ${gap.role}` : ''}: ${gap.staffed} of ${gap.minimum}${ratio}`
}

interface CoverageGapsProps {
  linesApplied: number
  gaps: readonly CoverageRow[]
  /** The facility's names for its units, where it keeps a list. */
  unitName?: (unitId: string) => string
  /** What the gaps are measured against: "this run", "the month as it stands". */
  basisLabel?: string
}

export function CoverageGaps({ linesApplied, gaps, unitName = (unitId) => unitId, basisLabel }: CoverageGapsProps) {
  const [open, setOpen] = useState(false)
  if (linesApplied === 0) {
    return null
  }
  if (gaps.length === 0) {
    return <p className="text-[11px] text-green">Every date meets the minimum staffing{basisLabel ? ` in ${basisLabel}` : ''}.</p>
  }
  const byDate = new Map<string, CoverageRow[]>()
  for (const gap of gaps) {
    byDate.set(gap.date, [...(byDate.get(gap.date) ?? []), gap])
  }
  return (
    <div className="text-[11px]">
      <button type="button" onClick={() => setOpen((value) => !value)} className="font-bold text-marine hover:text-marine-dk">
        <span className="text-coral">
          {byDate.size} {byDate.size === 1 ? 'date' : 'dates'} below minimum staffing
        </span>
        {basisLabel ? ` in ${basisLabel}` : ''} · {open ? 'Hide' : 'Show'}
      </button>
      {open && (
        <ul className="mt-1 flex flex-col gap-0.5 text-ink-mid">
          {[...byDate.entries()].map(([date, rows]) => (
            <li key={date}>
              <span className="font-bold text-ink">{describeDay(date)}</span> · {rows.map((row) => describeGap(row, unitName)).join(' · ')}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
